import React, { useState, useEffect } from 'react';
import {
  Eye, Search, Phone, MapPin, Globe, Star, MessageSquare, TrendingUp
} from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import apiService from './services/api';
import { Panel, Section, Badge, LoadingSpinner } from './components';

function GBPPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [chartView, setChartView] = useState('daily');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const d = await apiService.get('/amn/metrics/gbp/', { days });
        setData(d);
      } catch (err) {
        console.error('Error fetching GBP data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [days]);

  if (loading) return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto p-3" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <h1 className="text-lg font-semibold text-gray-900 mb-3">Google Business Profiles</h1>
      <Panel className="p-6"><LoadingSpinner /></Panel>
    </div>
  );

  if (!data) return null;

  // Filter totals and daily by selected location
  const t = selectedLocation === 'all'
    ? (data.totals || {})
    : (data.locations || []).find(l => l.id === selectedLocation) || {};

  const dailyData = selectedLocation === 'all'
    ? (data.daily || [])
    : (data.daily_by_location?.[selectedLocation] || []);

  // Aggregate daily data into monthly
  const monthlyData = (() => {
    const months = {};
    for (const d of dailyData) {
      const key = d.date?.slice(0, 7); // YYYY-MM
      if (!key) continue;
      if (!months[key]) months[key] = { date: key, total_views: 0, website_clicks: 0, phone_calls: 0, direction_requests: 0 };
      months[key].total_views += d.total_views || 0;
      months[key].website_clicks += d.website_clicks || 0;
      months[key].phone_calls += d.phone_calls || 0;
      months[key].direction_requests += d.direction_requests || 0;
    }
    return Object.values(months).sort((a, b) => a.date.localeCompare(b.date));
  })();

  const chartData = chartView === 'monthly' ? monthlyData : dailyData;

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Google Business Profiles</h1>
          <div className="flex items-center gap-2">
            <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800">
              <option value="all">All Locations</option>
              {(data?.locations || []).map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last 12 months</option>
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
          <Panel className="!p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Views</p>
                <p className="text-lg font-semibold text-gray-900">{(t.total_views || 0).toLocaleString()}</p>
              </div>
              <Eye className="w-5 h-5 text-blue-500" />
            </div>
          </Panel>
          <Panel className="!p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Search Views</p>
                <p className="text-lg font-semibold text-gray-900">{(t.search_views || 0).toLocaleString()}</p>
              </div>
              <Search className="w-5 h-5 text-green-500" />
            </div>
          </Panel>
          <Panel className="!p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Maps Views</p>
                <p className="text-lg font-semibold text-gray-900">{(t.maps_views || 0).toLocaleString()}</p>
              </div>
              <MapPin className="w-5 h-5 text-red-500" />
            </div>
          </Panel>
          <Panel className="!p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Website Clicks</p>
                <p className="text-lg font-semibold text-blue-700">{(t.website_clicks || 0).toLocaleString()}</p>
              </div>
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
          </Panel>
          <Panel className="!p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Phone Calls</p>
                <p className="text-lg font-semibold text-green-700">{(t.phone_calls || 0).toLocaleString()}</p>
              </div>
              <Phone className="w-5 h-5 text-green-600" />
            </div>
          </Panel>
          <Panel className="!p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Directions</p>
                <p className="text-lg font-semibold text-amber-700">{(t.direction_requests || 0).toLocaleString()}</p>
              </div>
              <MapPin className="w-5 h-5 text-amber-500" />
            </div>
          </Panel>
        </div>

        {/* Daily Chart */}
        {chartData && chartData.length > 1 && (
          <Panel className="mb-3">
            <div className="flex items-center justify-between px-3 pt-2">
              <h3 className="text-xs font-medium text-gray-900">
                {chartView === 'monthly' ? 'Monthly' : 'Daily'} Activity
                {selectedLocation !== 'all' && ` — ${(data.locations || []).find(l => l.id === selectedLocation)?.name || ''}`}
              </h3>
              <div className="flex bg-gray-100 rounded p-0.5">
                <button onClick={() => setChartView('daily')}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${chartView === 'daily' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  Daily
                </button>
                <button onClick={() => setChartView('monthly')}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${chartView === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                  Monthly
                </button>
              </div>
            </div>
            <Section tight>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="date" stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false}
                    tickFormatter={d => chartView === 'monthly'
                      ? new Date(d + '-15').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
                      : new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    interval="preserveStartEnd" minTickGap={40} />
                  <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} axisLine={false} width={35} />
                  <Tooltip
                    formatter={(v, name) => [v, {total_views:'Views', website_clicks:'Website Clicks', phone_calls:'Phone Calls', direction_requests:'Directions'}[name] || name]}
                    labelFormatter={d => chartView === 'monthly'
                      ? new Date(d + '-15').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                      : new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #e5e7eb' }} />
                  <Bar dataKey="total_views" fill="#3b82f6" radius={[3, 3, 0, 0]} name="total_views" />
                  <Line type="monotone" dataKey="website_clicks" stroke="#16a34a" strokeWidth={2} dot={false} name="website_clicks" />
                  <Line type="monotone" dataKey="phone_calls" stroke="#f59e0b" strokeWidth={2} dot={false} name="phone_calls" />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-4 mt-1 text-xs text-gray-500">
                <div className="flex items-center gap-1"><div className="w-3 h-2 bg-blue-500 rounded-sm" /><span>Views</span></div>
                <div className="flex items-center gap-1"><div className="w-2 h-0.5 bg-green-600" /><span>Website Clicks</span></div>
                <div className="flex items-center gap-1"><div className="w-2 h-0.5 bg-amber-500" /><span>Phone Calls</span></div>
              </div>
            </Section>
          </Panel>
        )}

        {/* Locations */}
        <Panel noPadding className="mb-3">
          <div className="px-3 py-2 border-b border-gray-200">
            <h2 className="text-xs font-medium text-gray-900">{data.profiles} Locations</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase">Reviews</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Views</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Search</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Maps</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Clicks</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Calls</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase">Directions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.locations.map((loc) => (
                  <tr key={loc.name} className="hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-900">{loc.name}</td>
                    <td className="px-2 py-1.5 text-center">
                      {loc.avg_rating ? (
                        <span className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-700">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />{loc.avg_rating}
                        </span>
                      ) : <span className="text-xs text-gray-400">-</span>}
                    </td>
                    <td className="px-2 py-1.5 text-center text-xs text-gray-700">{loc.review_count}</td>
                    <td className="px-2 py-1.5 text-right text-xs text-gray-900">{(loc.total_views || 0).toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right text-xs text-gray-600">{(loc.search_views || 0).toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right text-xs text-gray-600">{(loc.maps_views || 0).toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right text-xs text-blue-700 font-medium">{(loc.website_clicks || 0).toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right text-xs text-green-700 font-medium">{(loc.phone_calls || 0).toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right text-xs text-amber-700">{(loc.direction_requests || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        {/* Recent Reviews */}
        {data.reviews && data.reviews.length > 0 && (
          <Panel className="mb-3">
            <Section title={`Recent Reviews (${data.reviews.length})`} tight>
              <div className="space-y-2 mt-2">
                {data.reviews.map((review, idx) => (
                  <div key={idx} className="border border-gray-100 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-900">{review.reviewer || 'Anonymous'}</span>
                        <div className="flex items-center gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-3 h-3 ${s <= (review.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </div>
                      <span className="text-[10px] text-gray-400">
                        {review.date ? new Date(review.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      </span>
                    </div>
                    {review.comment && <p className="text-xs text-gray-700 mb-1">{review.comment}</p>}
                    {review.reply && (
                      <div className="bg-gray-50 rounded px-2 py-1 mt-1">
                        <p className="text-[10px] text-gray-500 font-medium mb-0.5">Reply</p>
                        <p className="text-xs text-gray-600">{review.reply}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">{review.location?.replace('Amour Mobile Nails 💝 Your Mobile Nails Service', '').trim() || ''}</p>
                  </div>
                ))}
              </div>
            </Section>
          </Panel>
        )}
      </div>
    </div>
  );
}

export default GBPPage;
